import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSessionsForViewer } from '../../hooks/useSessionsForViewer';
import { useRecordHandoffConsent } from '../../hooks/useRecordHandoffConsent';
import { SessionsSurface } from '../../components/sessions/SessionsSurface';
import { prefetchSessionLocationMap } from '../../components/sessions/lazySessionLocationMap';

/**
 * ECN — the converged /sessions surface (Persona & Sessions-Surface Convergence
 * redesign §2; built under CU-86aj1c9ev, replacing the Subtask-8 placeholder).
 * Both personas land here; it is the agent's entire shell and one destination
 * among many for a patient. URL is PHI-free (no patientId), per CU-86ahjxpk2.
 *
 * Deep-link focus: a session can be focused via `?focus={sessionId}`. The
 * as-built push deep link `/sessions/{sessionId}` (sendAgentAlert, CU-86ahjxpk2)
 * is handled by the sibling `sessions.$sessionId.tsx` route, which redirects to
 * this page with `?focus=` — one render path (convergence redesign §2.1 as-built,
 * CU-86aj1c9ev D-A: no separate /scan resolver).
 */
export interface SessionsSearch {
  focus?: string;
}

export const Route = createFileRoute('/_authenticated/sessions')({
  validateSearch: (search: Record<string, string>): SessionsSearch => ({
    focus: search.focus || undefined,
  }),
  component: Sessions,
});

function Sessions() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { focus } = Route.useSearch();
  const { data, isLoading, isError, refetch, isFetching } =
    useSessionsForViewer();
  // CU-86ajd3yt0: the external-handoff consent callable, owned here (the query
  // owner) and threaded into the pure surface for the "Open in maps" flow.
  const recordHandoffConsent = useRecordHandoffConsent();

  // Independent unauth gate (defense-in-depth) — mirrors the prior placeholder.
  // `undefined` = auth still initializing (wait); `null` = signed out (redirect).
  useEffect(() => {
    if (currentUser === null) {
      window.localStorage.setItem('requestedURL', window.location.href);
      navigate({ to: '/login', replace: true });
    }
  }, [currentUser, navigate]);

  // CU-86ajd3yt0: warm the lazy map chunk as soon as this surface mounts, before
  // any location card asks for it — so the emergency reader never waits on the
  // ~800KB maplibre download. Fire-and-forget; deduped with the lazy import.
  useEffect(() => {
    prefetchSessionLocationMap();
  }, []);

  if (!currentUser) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex w-full max-w-3xl flex-col items-center gap-4 text-center">
        <h1 className="text-2xl font-semibold text-sky-800">Sessions</h1>
        <p className="text-gray-500">Loading your scan sessions…</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex w-full max-w-3xl flex-col items-center gap-4 text-center">
        <h1 className="text-2xl font-semibold text-sky-800">Sessions</h1>
        <p className="text-gray-600">
          We couldn&apos;t load your sessions right now. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <SessionsSurface
      data={data}
      focusSessionId={focus}
      onRefresh={() => void refetch()}
      isRefreshing={isFetching}
      onRecordHandoffConsent={(payload) => recordHandoffConsent.mutateAsync(payload)}
      isRecordingConsent={recordHandoffConsent.isPending}
    />
  );
}
